import React from 'react';
import { BOOKING_URL } from '../../../constants/config';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  AlertTitle,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star'; // For bright smile/benefits
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'; // For safety/professionalism
import SpaIcon from '@mui/icons-material/Spa'; // For comfort / why choose us
import PaletteIcon from '@mui/icons-material/Palette'; // For aesthetic results
import EventSeatIcon from '@mui/icons-material/EventSeat'; // For in-office
import HomeIcon from '@mui/icons-material/Home'; // For take-home kits
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // For "Is it right for you?"
import LocalCafeIcon from '@mui/icons-material/LocalCafe'; // For causes of discoloration
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const TeethWhiteningPacificaDental = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const benefits = [
    'Achieve a noticeably brighter and more radiant smile.',
    'Safe and effective treatments supervised by dental professionals.',
    'Boost your self-confidence and enhance your overall appearance.',
    'Customized options for predictable and beautiful results.',
    'Relatively quick procedure with long-lasting effects with proper care.',
    'Remove stubborn stains from coffee, tea, wine, and aging.'
  ];

  const ourWhiteningOptions = [
    {
      title: 'In-Office Professional Whitening',
      description: 'Experience dramatic results in just one visit! Our in-office whitening uses a powerful, professional-grade gel, sometimes activated by a special light. We protect your gums and carefully monitor the process to achieve your desired shade safely and efficiently. You\'ll leave Pacifica Dental with a significantly brighter smile.',
      icon: <EventSeatIcon color="primary" sx={{ fontSize: 30 }}/>
    },
    {
      title: 'Custom Take-Home Whitening Kits',
      description: 'For those who prefer to whiten at their own pace, we offer custom-fitted take-home trays. We\'ll take impressions of your teeth to create trays that fit perfectly, ensuring even application of the professional-strength whitening gel and minimizing gum irritation. You\'ll receive detailed instructions to achieve optimal results conveniently at home.',
      icon: <HomeIcon color="primary" sx={{ fontSize: 30 }}/>
    }
  ];

  const whyDiscolor = [
    'Dark-colored foods and drinks (coffee, tea, red wine, berries).',
    'Tobacco use (smoking or chewing).',
    'The natural aging process, as enamel thins.',
    'Certain medications (e.g., tetracycline).',
    'Trauma to a tooth.',
    'Inadequate oral hygiene.'
  ];

  const isRightForYou = [
    'You have good overall oral health (healthy gums, no untreated cavities).',
    'You are looking for a cosmetic enhancement to brighten your smile.',
    'You understand that existing restorations (crowns, veneers, fillings) will not whiten.',
    'You are not pregnant or nursing.',
    'You are generally over 16-18 years of age (consult with our dentists).',
    'You have realistic expectations for the outcome.'
  ];

  const maintainingResults = [
    'Brush and floss regularly to prevent new stains.',
    'Limit consumption of staining foods and drinks, or rinse your mouth after.',
    'Avoid tobacco products.',
    'Attend regular dental cleanings at Pacifica Dental.',
    'Consider occasional touch-up treatments as recommended by our team.'
  ];
  
  const faqs = [
    {
      question: 'Is teeth whitening safe?',
      answer: 'Yes, professional teeth whitening at Pacifica Dental is safe when supervised by our qualified dental professionals. We use approved materials and techniques to protect your teeth and gums.'
    },
    {
      question: 'Will teeth whitening cause sensitivity?',
      answer: 'Some patients may experience temporary tooth sensitivity during or after the whitening process. This is usually mild and subsides shortly. We can recommend desensitizing products if needed.'
    },
    {
      question: 'How long do teeth whitening results last?',
      answer: 'Results vary depending on your diet, lifestyle, and oral hygiene habits. With proper maintenance, the effects can last for a significant period, often one to three years. Touch-up treatments can help maintain your desired shade.'
    },
    {
        question: 'How white will my teeth get?',
        answer: 'The degree of whitening depends on the original shade of your teeth, the type of staining, and the whitening system used. We aim for a naturally brighter smile that complements your features.'
    },
    {
        question: 'What is the difference between professional whitening and over-the-counter (OTC) products?',
        answer: 'Professional whitening at Pacifica Dental uses stronger, more effective agents and often custom-fitted trays for optimal results and safety. OTC products are generally less potent, may cause more sensitivity if not used correctly, and results can be less predictable.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Teeth Whitening | Pacifica Dental | Brighten Your Smile</title>
        <meta name="description" content="Achieve a dazzling, confident smile with professional teeth whitening at Pacifica Dental. We offer in-office and take-home options. Call (408) 869-0998." />
        <meta name="keywords" content="teeth whitening Pacifica Dental, professional teeth whitening, cosmetic dentistry Pacifica Dental, teeth bleaching, brighter smile, (408) 869-0998, San Jose area teeth whitening" /> {/* Consider adding city/region if applicable */}
      </Helmet>
      
      <Box 
        sx={{ 
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e9eef3 100%)',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              textAlign="center"
              gutterBottom
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: 'primary.main'
              }}
            >
              Illuminate Your Smile with Teeth Whitening at Pacifica Dental
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{ mb: 4, color: 'text.secondary', fontWeight: 500 }}
            >
              Safe, Effective, and Professional Whitening for a Dazzling Look
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 4, fontSize: '1.15rem', lineHeight: 1.8, color: 'text.primary' }}
            >
              A bright, white smile can significantly enhance your appearance and boost your confidence. At Pacifica Dental, we offer professional teeth whitening solutions designed to safely and effectively lighten discoloration caused by daily life. Whether you're preparing for a special event or simply wish to rejuvenate your smile, our experienced team will help you achieve the brilliant results you desire.
            </Typography>
            <Divider sx={{ my: 4 }} />

            {/* Benefits Section */}
            <Grid container spacing={isMobile ? 3 : 5} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    backgroundColor: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark',
                    color: 'white',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2}}>
                    Benefits of Professional Whitening at Pacifica Dental
                  </Typography>
                  <List dense>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index} sx={{ py: 0.5}}>
                        <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5}}>
                          <StarIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
                        </ListItemIcon>
                        <ListItemText primary={benefit} primaryTypographyProps={{fontSize: '1.05rem'}} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Our Whitening Options Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom color="primary.main" sx={{ fontWeight: 600, mb: 2}}>
                    Our Teeth Whitening Options
                  </Typography>
                  {ourWhiteningOptions.map((option, index) => (
                    <Box key={index} sx={{ mb: index === ourWhiteningOptions.length - 1 ? 0 : 3 }}>
                       <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                         {option.icon}
                        <Typography variant="h6" color="primary.dark" fontWeight="bold" sx={{fontSize: '1.15rem', ml: 1.5}}>
                          {option.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.95rem', lineHeight: 1.6, pl: {xs: 0, sm: '3.5rem'}}}> {/* Adjust pl for icon alignment */}
                        {option.description}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 5 }} /> 

            {/* Why Do Teeth Discolor? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Understanding Tooth Discoloration
              </Typography>
              <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e3f2fd' : '#2c3e50' }}>
                <Typography variant="body1" paragraph sx={{ mb: 2, fontSize: '1.1rem', color: 'text.secondary' }}>
                  Several factors can contribute to teeth losing their natural brightness over time:
                </Typography>
                <Grid container spacing={1}>
                    {whyDiscolor.map((item, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}> {/* md={4} for 3 items per row on larger screens */}
                            <List dense sx={{py:0}}>
                                <ListItem sx={{py:0.2}}>
                                    <ListItemIcon sx={{minWidth: 'auto', marginRight: 1, color: 'primary.main'}}>
                                        <LocalCafeIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={item} primaryTypographyProps={{fontSize: '1rem'}}/>
                                </ListItem>
                            </List>
                        </Grid>
                    ))}
                </Grid>
              </Paper>
            </Box>

            <Divider sx={{ my: 5 }} />
            
            {/* Is Teeth Whitening Right For You? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Is Professional Teeth Whitening Right for You?
              </Typography>
              <Alert severity="info" icon={<HelpOutlineIcon />} sx={{borderRadius: 1.5, p:2, mb:3 }} elevation={2}>
                <AlertTitle sx={{fontWeight: 'bold'}}>Important Considerations</AlertTitle>
                A consultation with our dentists at Pacifica Dental is the best way to determine if teeth whitening is suitable for your specific needs and dental health. We'll assess your smile and discuss your goals.
              </Alert>
              <Grid container spacing={2}>
                {isRightForYou.map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper elevation={1} sx={{p:1.5, display: 'flex', alignItems: 'center', height: '100%', borderRadius: 1.5}}>
                      <CheckCircleOutlineIcon color="success" sx={{mr: 1.5, fontSize: '1.8rem'}} />
                      <Typography variant="body1" sx={{fontSize: '1rem'}}>{item}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>


            <Divider sx={{ my: 5 }} />

            {/* Maintaining Your Brighter Smile Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Keeping Your Smile Bright and Beautiful
                </Typography>
                 <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e8f5e9' : '#1b5e20' }}>
                    <List>
                        {maintainingResults.map((tip, index) => (
                            <ListItem key={index}>
                                <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, color: 'primary.dark'}}>
                                    <PaletteIcon />
                                </ListItemIcon>
                                <ListItemText primary={tip} primaryTypographyProps={{fontSize: '1rem'}}/>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Box>
            
            <Divider sx={{ my: 5 }} />


            {/* FAQ Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Teeth Whitening FAQs at Pacifica Dental
              </Typography>
              {faqs.map((faq, index) => (
                <Accordion key={index} elevation={2} sx={{ mb: 1.5, borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary"/>}
                    aria-controls={`faq-panel${index}-content`}
                    id={`faq-panel${index}-header`}
                    sx={{ '& .MuiAccordionSummary-content': { fontWeight: 'medium', fontSize: '1.1rem' } }}
                  >
                    {faq.question}
                  </AccordionSummary>
                  <AccordionDetails sx={{backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : '#333' }}>
                    <Typography color="text.secondary" sx={{lineHeight: 1.7, fontSize: '1rem'}}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Why Choose Pacifica Dental Section */}
            <Box sx={{ mt: 6, p:3, background: theme.palette.mode === 'light' ? 'rgba(0, 123, 255, 0.05)' : 'rgba(100,100,100,0.1)', borderRadius:2 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb:3 }}>
                Why Choose Pacifica Dental for Your Teeth Whitening?
              </Typography>
              <Grid container spacing={3}>
                  {[
                      { title: "Experienced Dental Professionals", text: "Our skilled team at Pacifica Dental has extensive experience in providing safe and effective teeth whitening treatments.", icon: <VerifiedUserIcon color="primary" /> },
                      { title: "Personalized Treatment Plans", text: "We tailor your whitening treatment to your unique smile, desired shade, and sensitivity levels for optimal results.", icon: <PaletteIcon color="primary" /> },
                      { title: "High-Quality, Safe Materials", text: "We use clinically proven, professional-grade whitening agents that are gentle on your enamel.", icon: <SpaIcon color="primary" /> },
                      { title: "Stunning, Natural-Looking Results", text: "Our goal is to give you a noticeably brighter smile that still looks beautifully natural and enhances your features.", icon: <StarIcon color="primary" /> }
                  ].map(item => (
                      <Grid item xs={12} sm={6} key={item.title}>
                          <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
                              <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, marginTop: 0.5}}>
                                  {item.icon}
                              </ListItemIcon>
                              <Box>
                                  <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize: '1.1rem'}}>{item.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">{item.text}</Typography>
                              </Box>
                          </Box>
                      </Grid>
                  ))}
              </Grid>
            </Box>


            {/* Call to Action */}
            <Box sx={{ mt: 6, py: 4, textAlign: 'center', background: theme.palette.primary.main, color: 'white', borderRadius: 2 }}>
                <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{fontWeight: 600}}>
                    Ready for a Brighter, More Confident Smile?
                </Typography>
                <Typography variant="body1" sx={{mb:3, opacity: 0.9}}>
                    Contact Pacifica Dental today to schedule your teeth whitening consultation and discover your smile's true radiance!
                </Typography>
              <Button
                onClick={() => window.location.href = BOOKING_URL}
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  px: 5, 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  }
                }}
              >
                Schedule Your Whitening Consultation
              </Button>
              <Typography variant="body2" sx={{mt: 2, opacity: 0.8}}>
                Or call Pacifica Dental at <Link to="tel:4088690998" style={{color: 'white', fontWeight: 'bold'}}>(408) 869-0998</Link> to speak with our friendly team.
              </Typography>
            </Box>

          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default TeethWhiteningPacificaDental;